"use_client";

import style from './input.module.scss';
import React, { CSSProperties, FormEvent, KeyboardEventHandler, useEffect, useRef, useState } from 'react';
import { JSX } from "react/jsx-runtime";
import Datepicker from "../datepicker/Datepicker";
import { format, parse, subYears } from "date-fns";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import 'react-international-phone/style.css';
import Checkbox from '../../svg/Checkbox';
import DatepickerIcon from '../../svg/DatepickerIcon';
import Upload from '../../svg/Upload';
import Info from '../../svg/Info';
import { PhoneInput } from 'react-international-phone';

export enum InputSize {
  SMALL = "small",
  MEDIUM = "medium",
  LARGE = "large",
}

export interface RecommendedFields {
  listName: string;
  options: string[];
}

export enum InputTypes {
  TEXT = "text",
  CHECKBOX = "checkbox",
  NUMBER = "number",
  RADIO = "radio",
  BUTTON = "button",
  EMAIL = "email",
  PASSWORD = "password",
  IMAGE = "image",
  DATE = 'date',
  FILE = 'file'
}

export interface InputProps {
  value: string | string[] | File | number | undefined;
  defValue?: string | string[] | File | number | undefined;
  label?: string;
  type?: InputTypes;
  success?: boolean;
  error?: { has_error: boolean, message: string | JSX.Element };
  is_required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  checked?: boolean;
  recommendedFields?: RecommendedFields;
  classes?: string[];
  customStyle?: CSSProperties;
  dataTest?: string;
  onClick?: () => void;
  onInputClick?: () => void;
  onInput?: (e: FormEvent<HTMLInputElement>) => void;
  onChange?: (e: FormEvent<HTMLInputElement> | any) => void;
  onFocus?: (e: FormEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLLabelElement>) => void;
  onIconClick?: (date: Date) => void;
  onDateInput?: (value: string) => void;
  onPhoneInputAction?: (phone: any, meta: any) => void;
  label_text?: string;
  label_content?: JSX.Element;
  id?: string;
  placeholder?: string;
  icon?: JSX.Element;
  autofocus?: boolean;
  checkbox_options?: {
    fake_checkbox?: boolean;
    icon?: string;
    hex_color?: string;
    text: string;
  }
  radio_options?: {
    name: string;
    text: string;
    fake_radio?: boolean;
  };
  date_options?: {
    pastDate?: boolean;
    yesterday?: boolean;
    present?: boolean;
    showTimeInput?: boolean;
    selectedDate?: string;
  };
  customPhoneInput?: boolean;
  country?: string;
}

export const Input = ({
  label,
  value,
  defValue = undefined,
  type = InputTypes.TEXT,
  success = false,
  error = { has_error: false, message: '' },
  is_required = true,
  disabled = false,
  readOnly = false,
  checked = false,
  recommendedFields = { listName: '', options: [] },
  classes = [],
  customStyle,
  dataTest,
  onClick,
  onInputClick,
  onInput,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onDateInput,
  onIconClick,
  label_text,
  label_content,
  id,
  placeholder,
  icon,
  autofocus,
  checkbox_options,
  radio_options,
  date_options={
    pastDate: false,
    selectedDate: '',
    present: false,
    showTimeInput: false,
    yesterday: false,
  },
  customPhoneInput = false,
  onPhoneInputAction,
  country,
}: InputProps) => {
  const ref = useRef<HTMLInputElement | null>(null);

  const [visible, setVisible] = useState<boolean>(false);
  let today = new Date();
  const locale = useLocale();

  const onSelectDate = (date: Date) => {
    onDateInput(format(date, 'dd.MM.yyyy'));
  }
  
  return (
    <div
      className={`
        ${style.formGroup}
        ${success ? style.success : ''}
        ${readOnly ? style.readonly : ''}
        ${style[type]}
        ${error.has_error && (typeof error.message == 'string' ? error.message.length > 0 : true) ? style.error : ''}
        ${error.has_error ? 'error' : ''}
        ${classes.join(' ')}
        `}
      style={customStyle}
    >
      {
        label_text && label_text.length > 0 && type !== InputTypes.CHECKBOX && type !== InputTypes.RADIO ?
          <label htmlFor={id}>
            {label_text} {is_required ? '*' : ''}
            {icon ? <span className={style.icon} onClick={onClick}>{icon}</span> : <></>}
          </label>
          :
          <></>
      }
      {
        type == InputTypes.CHECKBOX
        ?
        <label htmlFor={id} className={``} tabIndex={0} onKeyDown={onKeyDown}>
          <input
            type={type}
            value={value as string | number | string[]}
            // defaultValue={defValue}
            disabled={disabled}
            {...(typeof checked === 'boolean' ? { checked } : {})}
            // readOnly={readOnly}
            // onClick={onClick}
            onChange={onChange}
            onInput={onInput}
            data-test={dataTest}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
            onClick={onInputClick}
            // list={recommendedFields.listName}
            id={id}
            placeholder={placeholder ?? ''}
          />
          <div className={style.fakeCheckbox}>
            {
              checkbox_options?.icon || checkbox_options?.hex_color
              ?
              <span className={`${style.color} ${checked ? style.checked : ''}`} style={{backgroundColor: checkbox_options.hex_color ?? 'transparent'}}>
                {checkbox_options.icon ? <img src={checkbox_options.icon} width={16} height={16} alt={checkbox_options.text} /> : <></>}
              </span>
              :
              <Checkbox height={20} width={20} checked={checked} hexColor={checked ? 'black' : 'white'} hexColor2={checked ? 'white' : 'black'} />
            }
          </div>
          <span className={style.labelText}>
            {label_content}
          </span>
        </label>
        : 
        <></>
      }
      {
        type == InputTypes.RADIO
        ?
        <label className={style.radioGroup} htmlFor={id} tabIndex={0} onKeyDown={onKeyDown}>
            <input 
              type={type}
              value={value as string | number | string[]}
              // defaultValue={defValue}
              disabled={disabled}
              {...(typeof checked === 'boolean' ? { checked } : {})}
              // readOnly={readOnly}
              // onClick={onClick}
              onChange={onChange}
              onInput={onInput}
              data-test={dataTest}
              onFocus={onFocus}
              onBlur={onBlur}
              onClick={onInputClick}
              onKeyUp={onKeyDown}
              name={radio_options.name}
              // list={recommendedFields.listName}
              id={id}
              placeholder={placeholder ?? ''}
            />
            {
              radio_options.fake_radio 
              ?
              <>
                <span className={style.fakeRadio}></span>
                {radio_options.text}
              </>
              :
              <span className={style.radioText}>
                  {radio_options.text}
              </span>

            }
          </label>
        :
        <></>
      }
      {
        type == InputTypes.FILE
        ?
        <input
          ref={ref}
          type={type}
          disabled={disabled}
          // checked={type == InputTypes.CHECKBOX ? checked : undefined}
          readOnly={readOnly}
          // onClick={onClick}
          className={style.upload}
          lang={locale}
          onChange={onChange}
          onInput={onInput}
          data-test={dataTest}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onClick={onInputClick}
          list={recommendedFields.listName}
          id={id}
          // autoComplete={type == InputTypes.PASSWORD ? 'off' : 'on'}
          placeholder={placeholder ?? ''}
          autoFocus={autofocus}
          accept="image/png,image/jpeg,.pdf"
        />
        : <></>
      }
      {
        type == InputTypes.DATE
        ?
        <>
          <div className={style.dateWrapper}>

            <input
                type={InputTypes.TEXT}
                value={value as string}
                // defaultValue={defValue as string | string[] | number}
                disabled={disabled}
                // checked={type == InputTypes.CHECKBOX ? checked : undefined}
                readOnly={readOnly}
                // onClick={onClick}
                // style={{width: dateState && dateState.day ? '20px' : '10px'}}
                onFocus={onFocus}
                onChange={onChange}
                onInput={onInput}
                data-test={dataTest}
                onBlur={onBlur}
                onClick={onInputClick}
                id={id}
                placeholder={placeholder}
                // maxLength={2}
              />
              
            </div>
        </>
        :
        <></>
      }
      {
        customPhoneInput
        ?
        <PhoneInput 
          value={value as string}
          onChange={onPhoneInputAction}
          onBlur={onBlur}
          onFocus={onFocus}
          defaultCountry='rs'
          className={style.phoneInput}
          // forceDialCode
          countrySelectorStyleProps={{
            buttonContentWrapperClassName: style.countriesWrapper,
            buttonClassName: style.phoneInputBtn,
            className: style.countrySelector
          }}
        />
        :
        type !== InputTypes.FILE && type !== InputTypes.CHECKBOX && type !== InputTypes.RADIO && type !== InputTypes.DATE
        ?
        <input
          type={!visible && type == InputTypes.PASSWORD ? InputTypes.PASSWORD : InputTypes.TEXT}
          value={value as string | string[] | number}
          defaultValue={defValue as string | string[] | number}
          disabled={disabled}
          // checked={type == InputTypes.CHECKBOX ? checked : undefined}
          readOnly={readOnly}
          // onClick={onClick}
          onChange={onChange}
          onInput={onInput}
          data-test={dataTest}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onClick={onInputClick}
          list={recommendedFields.listName}
          id={id}
          autoComplete={type == InputTypes.PASSWORD ? 'off' : 'on'}
          placeholder={placeholder ?? ''}
          autoFocus={autofocus}
        />
        : 
        <></>
      }
      {
        label && label.length > 0 ?
          <span className={style.placeholder}>{label}{is_required ? '*' : ''}</span>
          : <></>
      }
      {
        type == InputTypes.PASSWORD ?
          <figure className={`${style.passwordEye}`} onClick={() => setVisible(!visible)}>
            <Image src={visible ? '/img/icons/eye-slashed.svg' : '/img/icons/eye.svg'} width={24} height={24} alt="password" />
            {/* <PasswordEye hexColor={visible ? '#484848' : "#d9d9d9"} /> */}
          </figure>
          :
          <></>
      }
      {
        type == InputTypes.DATE ?
        <figure className={`${style.dateIcon}`} onClick={() => setVisible(!visible)}>
          <DatepickerIcon />
        </figure>
          :
          <></>
      }
      {
        type == InputTypes.FILE ?
        <figure className={style.dateIcon} onClick={() => ref.current.click()}>
          <Upload />
        </figure>
        : <></>
      }
      {
        visible && type == InputTypes.DATE
        ?
        <Datepicker 
          onDateSelect={onSelectDate}
          selectedDate={date_options.selectedDate}
          onOpenCloseCalendar={setVisible}
          open={visible}
          showTimeInput={date_options.showTimeInput}
          openToDate={date_options.pastDate && !date_options.present ? new Date(date_options.yesterday ? today.getFullYear() : today.getFullYear() - 18, today.getMonth(), date_options.yesterday ? today.getDate() - 1 : today.getDate()) : new Date(today.getFullYear(), today.getMonth(), today.getDate() + (!date_options.present ? 1 : 0))}
          minDate={date_options.pastDate ? subYears(new Date(), date_options.yesterday ? 26 : 100) : new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1)}
          maxDate={date_options.pastDate && !date_options.present ? new Date(date_options.yesterday ? today.getFullYear() : today.getFullYear() - 18, today.getMonth(), date_options.yesterday ? today.getDate() - 1 : today.getDate()) : (date_options.present ? today : new Date(today.getFullYear() + 10, today.getMonth(), today.getDate()))}
        />
        : <></>
      }
      {
        recommendedFields
          ?
          <datalist id={recommendedFields.listName}>
            {
              recommendedFields.options.map((option, index) => {
                return <option key={index} value={option} />
              })
            }
          </datalist>
          :
          <></>
      }
      <div className="errorMessage">
        {(typeof error.message == 'string' && error.message.length > 0) ? <span><Info width={12} height={12} /></span> : <></>}
        {error.message}
      </div>
    </div>
  );
};

export default Input;